import { useState } from 'react';
import type { StoryRunDoc } from '@spirits/shared';

// Header control that reveals the run's invite link + join code and copies them.
export function ShareRunButton({ story }: { story: StoryRunDoc & { id: string } }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const link =
    typeof window !== 'undefined' ? `${window.location.origin}/play/${story.id}` : `/play/${story.id}`;

  async function copy(value: string, what: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(what);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      setCopied(null);
    }
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded-full border border-slate-700 text-slate-300 hover:text-slate-100 hover:border-slate-500 px-2.5 py-1 text-xs leading-none flex items-center gap-1.5"
        title="invite others to this run"
      >
        <span aria-hidden>⇪</span>
        <span className="hidden sm:inline">share</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 z-40 w-64 rounded-xl border border-slate-700 bg-slate-900 p-3 shadow-xl space-y-2">
            <p className="text-[10px] uppercase tracking-widest text-slate-500">invite spirits</p>
            <button
              type="button"
              onClick={() => copy(link, 'link')}
              className="w-full text-left rounded-md border border-slate-700/60 bg-slate-800/60 hover:bg-slate-700/60 px-2.5 py-1.5 text-xs text-slate-200"
            >
              {copied === 'link' ? 'link copied ✓' : 'Copy invite link'}
            </button>
            <button
              type="button"
              onClick={() => copy(story.joinCode, 'code')}
              className="w-full text-left rounded-md border border-slate-700/60 bg-slate-800/60 hover:bg-slate-700/60 px-2.5 py-1.5 text-xs text-slate-200 flex items-center justify-between"
            >
              <span>{copied === 'code' ? 'code copied ✓' : 'Copy join code'}</span>
              <span className="font-mono tracking-widest text-amber-200/90">{story.joinCode}</span>
            </button>
            <p className="text-[10px] text-slate-600 leading-snug">
              {story.visibility === 'public'
                ? 'This run is public — it also shows on the Public Runs page.'
                : 'This run is unlisted — only people with the link or code can join.'}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
